import React, { useMemo, useState, useRef, useEffect } from 'react';
import { usePost } from '../API';
import { ThreeColumnPage } from '../Components/ThreeColumnPage';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { useLocalizedStrings } from '../Localization';
import { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';
import { Card, CardBody, CardHeader, Button, DataTable, Loading, Heading, Tooltip, Input, RadioGroup, Combobox, Icon, Checkbox } from '@aidkitorg/component-library';
import { type User } from '@aidkitorg/aidkit/lib/users/UserService';
import { type UserGroup } from '@aidkitorg/aidkit/lib/users/UserGroupService';
import { TagBadge } from './TagsUtil';
import { useDebouncedCallback } from '../Hooks/Debounce';
import { emailValidator } from '../utils/emailValidator';
import { phoneValidator } from '../utils/phoneValidator';
import { ColumnDef } from '@tanstack/react-table';

// Helper function to check if user is hidden from non-admins
export const isUserHiddenFromNonAdmins = (user: User): boolean => {
  try {
    const metadata = user.metadata ? JSON.parse(user.metadata) : {};
    return metadata.HiddenFromNonAdmins === true;
  } catch (e) {
    return false;
  }
};

// Main Component fetches orgs.
const UserManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const getOrgs = usePost('/organization/list');
  const getEditableTags = usePost('/tags/list');
  const [editableTags, setEditableTags] = useState<string[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [viewMode, setViewMode] = useState<'group' | 'list'>('group');

  useAsyncEffect(async () => {
    const tags = await getEditableTags({});
    if (Array.isArray(tags)) {
      setEditableTags(tags);
    }
  }, []);

  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    const orgs = await getOrgs({});
    if (Array.isArray(orgs)) {
      setOrgs(orgs);
      setSelectedOrg(orgs[0]);
    }
    setLoading(false);
  }, []);

  if (loading) return <Loading labels={[{ content: L.user.fetching_users }]} />;

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-6 w-full mt-4">
        <div className="flex-1 max-w-4xl">
          <Heading className="mb-3">{L.user.user_management}</Heading>
          
          {/* Main description */}
          <div className="text-sm text-gray-700 mb-4 leading-relaxed">
            {L.user.user_management_detailed_description}
          </div>

          {/* Definitions section */}
          <div className="space-y-3 mb-4">
            <div>
              <div className="text-sm font-medium text-gray-900 mb-1">{L.user.permissions_heading}</div>
              <div className="text-xs text-gray-600">{L.user.permissions_description}</div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-900 mb-1">{L.user.user_group_heading}</div>
              <div className="text-xs text-gray-600">{L.user.user_group_description}</div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-900 mb-1">{L.user.user_heading}</div>
              <div className="text-xs text-gray-600">{L.user.user_description}</div>
            </div>
          </div>
        </div>
        
        <div className="lg:flex-shrink-0">
          <RadioGroup
            className="w-full sm:min-w-sm"
            label=''
            value={viewMode} 
            onChange={value => setViewMode(value as 'group' | 'list')}
            options={[
              { label: L.user.view_as_group, value: 'group' },
              { label: L.user.view_as_list, value: 'list' },
            ]}
          />
        </div>
      </div>
      <div>
        {viewMode === 'list' ? (
          <div className="w-full">
            <UsersList />
          </div>
        ) : (
          <div className="w-full">
            {orgs.length > 1 && <div className='max-w-sm'>
              <Combobox
                multiple={false}
                label={L.user.select_an_organization_to_view_user_groups}
                options={orgs.map(org => ({ label: org.name, value: org.uid }))}
                onChange={option => setSelectedOrg(orgs.find(org => org.uid === option.value) || null)}
                value={selectedOrg ? { label: selectedOrg.name, value: selectedOrg.uid } : { label: orgs[0].name, value: orgs[0].uid }}
              />
            </div>}
            {selectedOrg && <OrganizationBody org={selectedOrg} editableTags={editableTags} />}
          </div>
        )}
      </div>
    </div>
  );
};

function UsersList() {
  const [users, setUsers] = useState<User[]>([]);
  const getUsers = usePost('/user/list');
  const getOrgs = usePost('/organization/list');
  const getEditableTags = usePost('/tags/list');
  const updateUserApi = usePost('/user/update');
  const L = useLocalizedStrings();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [organization, setOrganization] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [editableTags, setEditableTags] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useAsyncEffect(async () => {
    const [listOrgsResponse, editableTagsResponse] = await Promise.all([
      getOrgs({}),
      getEditableTags({})
    ]);
    
    if (Array.isArray(listOrgsResponse)) {
      setOrgs(listOrgsResponse);
    }
    
    if (Array.isArray(editableTagsResponse)) {
      setEditableTags(editableTagsResponse);
      setIsAdmin(editableTagsResponse.includes('admin'));
    }
  }, []);

  const fetchUsers = async (org: string, searchVal: string) => {
    const listUsersResponse = await getUsers({
      // need to pass undefined instead of the empty string from the dropdown to get all users.
      organization: org || undefined,
      filter: searchVal || undefined,
    });
    if (Array.isArray(listUsersResponse)) {
      setUsers(listUsersResponse);
    }
  };

  const debouncedFetchUsers = useDebouncedCallback(fetchUsers, 300, []);

  useEffect(() => {
    debouncedFetchUsers(organization, search);
  }, [organization, search]);


  const columns = useMemo(() => {
    const baseColumns: ColumnDef<User>[] = [
      {
        header: L.user.name,
        accessorKey: 'name',
      },
      {
        header: L.user.email,
        accessorKey: 'email',
      },
      {
        header: L.user.phone,
        accessorKey: 'phone',
      },
      {
        header: L.user.user_group,
        accessorKey: 'user_group_name',
      },
      {
        header: L.user.organization,
        accessorKey: 'organization_name',
      },
    ];

    // Add Hidden column for admins only
    if (isAdmin) {
      baseColumns.push({
        header: () => (
          <Tooltip text={L.user.hidden_from_non_admins} asChild={false}>
            <span>{L.user.hidden}</span>
          </Tooltip>
        ),
        accessorKey: 'hidden',
        cell: ({ row }: any) => {
          const user = row.original as User;
          const isHidden = isUserHiddenFromNonAdmins(user);
          return (
            <div className="flex items-center justify-center">
              {isHidden ? (
                <Icon name="CheckIcon" className="w-4 h-4" />
              ) : null}
            </div>
          );
        },
      });
    }

    // Add actions column
    baseColumns.push({
      header: L.user.actions,
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <Button size="sm" variant="link" href={`/user/${row.original.uid}`} openInNewWindow>{L.user.view_user_details}</Button>
      ),
    });

    return baseColumns;
  }, [users, L, isAdmin]);

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row gap-2 items-end mb-4">
        <div className="w-full max-w-sm">
          <Input
            label={L.user.search}
            placeholder={L.user.search_placeholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {orgs.length > 1 && <div className="w-full max-w-sm">
          <Combobox
            multiple={false}
            label={L.user.organization}
            options={[
              { label: L.user.all, value: '' },
              ...orgs.map(org => ({ label: org.name, value: org.uid })),
            ]}
            value={organization
              ? { label: orgs.find(o => o.uid === organization)?.name || '', value: organization }
              : { label: L.user.all, value: '' }
            }
            onChange={option => setOrganization(option.value)}
          />
        </div>}
      </div>
      <Card >
        <DataTable
          pageSize={100}
          columns={columns}
          data={users}
          getRowId={(row: User, index: number) => {
            return row.uid ?? String(index);
          }}
        />
      </Card>
    </div>
  );
}

function OrganizationBody({ org, editableTags }: { org: Organization, editableTags: string[]   }) {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const getGroups = usePost('/user-group/list');
  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    setLoading(true);
    const userGroups = await getGroups({ organization: org.uid });
    if (Array.isArray(userGroups)) {
      setGroups(userGroups);
    }
    setLoading(false);
  }, [org.uid]);

  const addNewGroup = () => {
    setGroups([
      {
        id: 0,
        name: '',
        organization: org.uid,
        permissions: '',
        metadata: {},
        canEdit: true,
        created_at: new Date().toISOString(),
      },
      ...groups,
    ]);
  };

  if (loading) return <Loading labels={[{ content: L.user.fetching_users }]} />;

  return (
    <div>
      <div className="flex justify-end mb-4 mr-4">
        <Button variant="secondary" onClick={addNewGroup}>{L.user.add_group}</Button>
      </div>
      {groups.map(group => (
        <UserGroupCard key={group.id} group={group} setGroups={setGroups} groups={groups} editableTags={editableTags} />
      ))}
    </div>
  );
}

function UserGroupCard({ group, setGroups, groups, editableTags }: { group: UserGroup, setGroups: (groups: UserGroup[]) => void, groups: UserGroup[], editableTags: string[] }) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const getUsers = usePost('/user/list');
  const L = useLocalizedStrings();
  const isAdmin = editableTags.includes('admin');
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<User>>({});
  const updateUser = usePost('/user/update');
  const updateUserGroup = usePost('/user-group/update');
  const createUserGroup = usePost('/user-group/create');
  const createUser = usePost('/user/create');
  const deleteUser = usePost('/user/delete');
  const deleteUserGroup = usePost('/user-group/delete');
  const [editingGroup, setEditingGroup] = useState(group.id === 0); // On new group creation id is 0
  const editUserValuesRef = useRef<Partial<User>>({});
  const editGroupValuesRef = useRef<UserGroup>(group);
  const editingUserHiddenRef = useRef<boolean>(false);
  const [canEditGroup, setCanEditGroup] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<{ label: string, value: string }[]>(
    group.permissions
      ? group.permissions.split(',').map(p => ({ label: p, value: p }))
      : []
  );
  
  // User validation state - use ref to avoid table re-renders
  const userFormValidRef = useRef({
    name: false,
    email: false,
    phone: false
  });
  
  // Callbacks to notify components of validation changes
  const validationCallbackRef = useRef<(() => void) | null>(null);
  const cellValidationCallbacksRef = useRef<{[key: string]: (isValid: boolean) => void}>({});
  
  // Group validation state
  const [groupFormValid, setGroupFormValid] = useState({
    name: false
  });

  const isCreatingGroup = group.id === 0;
  const isCreatingUser = editingUid === '0';

  useEffect(() => {
    setCanEditGroup(group.canEdit || false);
  }, [group.canEdit]);

  // Validate user form when values change
  const validateUserForm = React.useCallback((updatedField?: {key: string, value: string}) => {
    if (!editingUid) return;
    
    const currentValues = {
      name: editUserValuesRef.current.name || draft.name || '',
      email: editUserValuesRef.current.email || draft.email || '',
      phone: editUserValuesRef.current.phone || draft.phone || ''
    };
    
    // If we have an updated field, use that value instead
    if (updatedField) {
      (currentValues as any)[updatedField.key] = updatedField.value;
    }
        
    const newValidation = {
      name: currentValues.name.trim().length > 0,
      email: emailValidator(currentValues.email),
      phone: !currentValues.phone || phoneValidator(currentValues.phone.replace(/\D/g, ''))
    };
        
    userFormValidRef.current = newValidation;
    
    // Notify save button if callback is registered. Can't use state cause it will cause a re-render of the table.
    if (validationCallbackRef.current) {
      validationCallbackRef.current();
    }
    
    // Notify individual cells of their validation state
    Object.keys(newValidation).forEach(field => {
      const callback = cellValidationCallbacksRef.current[field];
      if (callback) {
        callback(newValidation[field as keyof typeof newValidation]);
      }
    });
  }, [editingUid, draft]);

  // Validate group form when group values change
  useEffect(() => {
    if (!editingGroup) return;
    
    setGroupFormValid({
      name: (editGroupValuesRef.current.name || '').trim().length > 0
    });
  }, [editingGroup, selectedPermissions]);

  useAsyncEffect(async () => {
    setLoading(true);
    const users = await getUsers({ organization: group.organization, user_group: group.id });
    if (Array.isArray(users)) {
      setUsers(users);
    }
    setLoading(false);
  }, [group.id, group.organization]);

  const columns = useMemo(() => {
    const baseColumns: ColumnDef<User>[] = [
      {
        header: L.user.name,
        accessorKey: 'name',
        cell: ({ row }: any) => {
          const orig = row.original as User;
          if (editingUid === orig.uid) {
            return (
              <EditableCell
                initialValue={draft.name ?? orig.name}
                onValueChange={(val: string) => { 
                  editUserValuesRef.current.name = val;
                  validateUserForm({key: 'name', value: val});
                }}
                hasError={!userFormValidRef.current.name}
                errorMessage={L.questions.textentry.this_field_is_required}
                fieldName="name"
                cellValidationCallbacksRef={cellValidationCallbacksRef}
              />
            );
          }
          return orig.name;
        },
        size: 300
      },
      {
        header: L.user.email,
        accessorKey: 'email',
        cell: ({ row }: any) => (
          editingUid === row.original.uid ? (
            <EditableCell
              initialValue={draft.email ?? ''}
              onValueChange={val => { 
                editUserValuesRef.current.email = val;
                validateUserForm({key: 'email', value: val});
              }}
              hasError={!userFormValidRef.current.email}
              errorMessage={L.questions.textentry.must_be_a_valid_email}
              fieldName="email"
              cellValidationCallbacksRef={cellValidationCallbacksRef}
            />
          ) : (
            row.original.email
          )
        ),
        size: 300
      },
      {
        header: L.user.phone,
        accessorKey: 'phone',
        cell: ({ row }: any) => (
          editingUid === row.original.uid ? (
            <EditableCell
              initialValue={draft.phone ?? ''}
              onValueChange={val => { 
                editUserValuesRef.current.phone = val;
                validateUserForm({key: 'phone', value: val});
              }}
              hasError={!userFormValidRef.current.phone}
              errorMessage={L.user.must_be_a_valid_phone_number}
              fieldName="phone"
              cellValidationCallbacksRef={cellValidationCallbacksRef}
            />
          ) : (
            row.original.phone
          )
        ),
        size: 300
      },
    ];

    // Add Hidden column for admins only
    if (isAdmin) {
      baseColumns.push({
        header: () => (
          <Tooltip text={L.user.hidden_from_non_admins} asChild={false}>
            <span>{L.user.hidden}</span>
          </Tooltip>
        ),
        accessorKey: 'hidden',
        cell: ({ row }: any) => {
          const user = row.original as User;
          const isHidden = isUserHiddenFromNonAdmins(user);
          
          if (editingUid === user.uid) {
            // Editable checkbox when row is being edited
            return (
              <EditableCheckbox
                user={user}
                editingUserHiddenRef={editingUserHiddenRef}
                editUserValuesRef={editUserValuesRef}
              />
            );
          } else {
            // Display-only icon when not editing
            return (
              <div className="flex items-center justify-center">
                {isHidden ? (
                  <Icon name="CheckIcon" className="w-4 h-4" />
                ) : null}
              </div>
            );
          }
        },
      });
    }

    // Add actions column
    baseColumns.push({
      header: L.user.actions,
      cell: ({ row }: any) => (
        editingUid === row.original.uid ? (
          <div className="flex gap-2">
            <SaveButtonWithValidation 
              onClick={saveUserEdit}
              userFormValidRef={userFormValidRef}
              validationCallbackRef={validationCallbackRef}
              label={L.applicant.notes.save}
            />
            <Button onClick={cancelUserEdit} size="sm" variant="secondary" className="ml-2">{L.applicant.notes.cancel}</Button>
            {/* hide delete button when creating new user */}
            { !isCreatingUser && <Button
              onClick={async () => {
                if (window.confirm(L.user.confirm_delete_user)) {
                  await deleteUser({ uid: row.original.uid });
                  setUsers(users.filter(u => u.uid !== row.original.uid));
                  setEditingUid(null);
                }
              }}
              size="sm"
              variant="danger"
              className="ml-2"
            >
              {L.user.delete_user}
            </Button> }
          </div>
        ) : (
          <div className='flex gap-2'>
            {((row.original.source !== 'User Management') || !canEditGroup) ?
              <Tooltip text={!canEditGroup ? L.user.edit_group_disabled : L.user.edit_user_disabled} asChild={true}>
                <Button 
                  onClick={() => {}} 
                  size="sm" 
                  variant="secondary" 
                  isDisabled={true}
                >{L.user.edit_user}</Button>
              </Tooltip>
              : 
              <Button 
                onClick={() => startUserEdit(row.original)} 
                size="sm" 
                variant="secondary" 
                isDisabled={!!editingUid}
              >{L.user.edit_user}</Button>
            }
            <Button size="sm" variant="link" href={`/user/${row.original.uid}`} openInNewWindow>{L.user.view_user_details}</Button>
          </div>
        )
      ),
      size: 300
    });

    return baseColumns;
  }, [editingUid, draft, setDraft, canEditGroup, isCreatingUser, L, validateUserForm, isAdmin]);

  const addNewUser = () => {
    setDraft({})
    editUserValuesRef.current = {}; // Clear ref values
    
    // Initialize hidden state for new user (false by default)
    editingUserHiddenRef.current = false;
    
    setUsers([
      {
        uid: '0',
        name: '',
        email: '',
        phone: '',
        user_group: group.id,
        organization: group.organization,
        permissions: group.permissions,
        metadata: '',
        password: '',
        source: 'User Management',
      },
      ...users
    ]);
    setEditingUid('0');
    
    // Initialize form validity for new user (all invalid initially)
    userFormValidRef.current = {
      name: false,
      email: false,
      phone: true // Phone is optional, so initially valid
    };
    
    // Notify save button of initial state
    if (validationCallbackRef.current) {
      validationCallbackRef.current();
    }
  };

  const startUserEdit = (user: User) => {
    // if we start editing a user while we are creating one, we need to remove the draft row.
    if (isCreatingUser) {
      setUsers(users.filter(u => u.uid !== '0'));
    }
    setEditingUid(user.uid);
    setDraft({ ...user });
    editUserValuesRef.current = {}; // Clear ref values for new edit
    
    // Initialize hidden state
    editingUserHiddenRef.current = isUserHiddenFromNonAdmins(user);
    
    // Initialize form validity based on current user data
    userFormValidRef.current = {
      name: (user.name || '').trim().length > 0,
      email: emailValidator(user.email || ''),
      phone: !user.phone || phoneValidator(user.phone.replace(/\D/g, ''))
    };
    
    // Notify save button of initial state
    if (validationCallbackRef.current) {
      validationCallbackRef.current();
    }
  };

  const cancelUserEdit = () => {
    // If we are canceling editing a new user, we need to remove the draft row. 
    if (isCreatingUser) {
      setUsers(users.filter(u => u.uid !== '0'));
    }
    setEditingUid(null);
    setDraft({});
    editUserValuesRef.current = {};
    editingUserHiddenRef.current = false;
  };

  const saveUserEdit = async () => {
    if (editingUid) {
      const updatedUser = { uid: editingUid, user_group: group.id, ...editUserValuesRef.current };
      let updateResult: User;
      if (isCreatingUser) {
        updateResult = await createUser({
          name: updatedUser.name ?? '',
          phone: updatedUser.phone ?? '',
          email: updatedUser.email ?? '',
          user_group: group.id,
          metadata: updatedUser.metadata ?? '',
          organization: group.organization,
        });
      } else {
        updateResult = await updateUser(updatedUser);
      }
      if (updateResult?.uid) {
        if (isCreatingUser) {
          setUsers([updateResult, ...users.filter(u => u.uid !== '0')]);
        } else {
          setUsers(users.map(u => u.uid === updateResult.uid ? updateResult : u));
        }
      } else if (isCreatingUser) {
        setUsers(users.filter(u => u.uid !== '0'));
      }
    }
    setEditingUid(null);
    editUserValuesRef.current = {};
  };

  const startGroupEdit = () => {
    setEditingGroup(true);
    // Initialize form validity based on current group state
    setGroupFormValid({
      name: group.name.trim().length > 0
    });
  };

  const cancelGroupEdit = () => {
    if (isCreatingGroup) {
      // If we are editing a new group, we need to remove it from the list
      setGroups(groups.filter(g => g.id !== 0));
    }
    setEditingGroup(false);
  };

  const saveGroupEdit = async () => {
    if (editingGroup) {
      let updateResult: UserGroup;
      if (isCreatingGroup) {
        const {id, ...createParams} = editGroupValuesRef.current;
        updateResult = await createUserGroup(createParams);
      } else {
        updateResult = await updateUserGroup(editGroupValuesRef.current);
      }
      if (updateResult?.id) {
        if (isCreatingGroup) {
          setGroups([updateResult, ...groups.filter(g => g.id !== 0)]); 
        } else {
          setGroups(groups.map(g => g.id === updateResult.id ? updateResult : g));
        }
      }
    }
    setEditingGroup(false);
  };

  const groupEditButtons = editingGroup ? 
    <div className="flex justify-end gap-2">
      { !isCreatingGroup && <Button
        isDisabled={!canEditGroup}
        onClick={async () => {
          if (window.confirm(L.user.confirm_delete_group)) {
            await deleteUserGroup({ id: group.id });
            setGroups(groups.filter(g => g.id !== group.id));
          }
        }}
        size="md"
        variant="danger"
        className="ml-2"
      >
        {L.user.delete_group}
      </Button>
      }
      <Button 
        isDisabled={!canEditGroup || Object.values(groupFormValid).some(v => !v)} 
        onClick={saveGroupEdit} 
        size="md" 
        variant="primary"
      >
        {L.user.save}
      </Button>
      <Button isDisabled={!canEditGroup} onClick={cancelGroupEdit} size="md" variant="secondary">{L.user.cancel}</Button>
    </div>
    :
    <div className="flex justify-end gap-2">
      <Button isDisabled={!canEditGroup || !!editingUid} onClick={addNewUser} size="md" variant="secondary">{L.user.add_user}</Button>
      <Button isDisabled={!canEditGroup} onClick={startGroupEdit}  size="md" variant="secondary">{L.user.edit_group}</Button>
    </div>;

  if (loading) return <Loading labels={[{ content: L.user.fetching_users }]} />;

  return (
    <Card className="mt-4">
      <CardHeader 
        title={
          <div>
            <label className="block text-sm font-medium mb-1">
              <span>{L.user.group_name}</span>
            </label><Heading level='h3' className='flex justify-right w-full'>
              {group.name}
            </Heading>
          </div>
        }
        actionButton={canEditGroup ? 
          groupEditButtons
          : <Tooltip text={L.user.edit_group_disabled} asChild={true}>{groupEditButtons}</Tooltip>
        } />
      <CardBody>
        { editingGroup ? (
          <form
            onSubmit={e => {
              e.preventDefault();
              saveGroupEdit();
            }}
            className="space-y-4 mt-10"
          >
            <div>
              <label className="block text-sm font-medium mb-1">{L.user.name}</label>
              <input
                className={`border rounded px-2 py-1 w-full ${!groupFormValid.name ? 'border-red-500' : ''}`}
                defaultValue={group.name}
                onChange={e => { 
                  editGroupValuesRef.current.name = e.target.value;
                  setGroupFormValid({name: e.target.value.trim().length > 0});
                }}
              />
              {!groupFormValid.name && (
                <div className="text-red-500 text-xs mt-1">{L.questions.textentry.this_field_is_required}</div>
              )}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium mb-1">
                <Tooltip text={L.user.group_permissions_description} asChild={true}>
                  <span>{L.user.group_permissions}</span>
                </Tooltip>
              </label>
              <div className="flex flex-wrap gap-2">
                {editableTags.map(tag => {
                  const selected = selectedPermissions.some(p => p.value === tag);
                  return (
                    <label key={tag} className={`cursor-pointer ${selected ? 'bg-blue-100' : 'bg-gray-100'} rounded px-2 py-1 flex items-center gap-1`}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          let newSelected;
                          if (selected) {
                            newSelected = selectedPermissions.filter(p => p.value !== tag);
                          } else {
                            newSelected = [...selectedPermissions, { label: tag, value: tag }];
                          }
                          setSelectedPermissions(newSelected);
                          editGroupValuesRef.current.permissions = newSelected.map(v => v.value).join(',');
                        }}
                        className="mr-1"
                      />
                      <span >
                        <TagBadge tag={tag} />
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </form>
        ) : (
          <div className="mb-2 mt-10">
            <div className='text-sm font-medium mb-2 w-full'>
              <Tooltip text={L.user.group_permissions_description}><span>{L.user.group_permissions}</span></Tooltip>
            </div>
            {group.permissions?.length > 0 && group.permissions.split(',').map(permission => 
              <TagBadge key={permission} tag={permission} />
            )}
          </div>
        )}
        <div className='mt-20'>
          {users.length ?
            <DataTable
              columns={columns}
              data={users}
              getRowId={(row: User, index: number) => {
                return row.uid ?? String(index);
              }}
              pageSize={100}
            /> : 
          // No users and editing means we are adding a new group, we don't want to show the no users message yet
            editingGroup ? 
              <></> : 
              <div className="text-center text-sm text-gray-500 mt-10 mb-10">{L.user.no_users_in_group}</div>
          }
        </div>
      </CardBody>
    </Card>
  );
}

type EditableCellProps = {
  initialValue: string;
  onValueChange: (value: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  fieldName?: string;
  cellValidationCallbacksRef?: React.MutableRefObject<{[key: string]: (isValid: boolean) => void}>;
};

type SaveButtonProps = {
  onClick: () => void;
  userFormValidRef: React.MutableRefObject<{name: boolean, email: boolean, phone: boolean}>;
  validationCallbackRef: React.MutableRefObject<(() => void) | null>;
  label: string;
};

const SaveButtonWithValidation: React.FC<SaveButtonProps> = ({ onClick, userFormValidRef, validationCallbackRef, label }) => {
  const [localDisabled, setLocalDisabled] = React.useState(true);
  
  // Register callback and check initial state
  useEffect(() => {
    const checkValidation = () => {
      const validationState = userFormValidRef.current;
      const allValid = Object.values(validationState).every(v => v === true);
      setLocalDisabled(!allValid);
    };
    
    // Register callback
    validationCallbackRef.current = checkValidation;
    
    // Check initial state
    checkValidation();
    
    // Cleanup
    return () => {
      validationCallbackRef.current = null;
    };
  }, [userFormValidRef, validationCallbackRef]);

  return (
    <Button 
      onClick={onClick}
      size="sm" 
      variant="primary"
      isDisabled={localDisabled}
    >
      {label}
    </Button>
  );
};

const EditableCell: React.FC<EditableCellProps> = ({ initialValue, onValueChange, hasError, errorMessage, fieldName, cellValidationCallbacksRef }) => {
  const [value, setValue] = React.useState(initialValue);
  const [localError, setLocalError] = React.useState(hasError);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    setLocalError(hasError);
  }, [hasError]);

  // Register for validation callbacks if fieldName and callback ref are provided
  React.useEffect(() => {
    if (fieldName && cellValidationCallbacksRef) {
      const callback = (isValid: boolean) => {
        setLocalError(!isValid);
      };
      
      cellValidationCallbacksRef.current[fieldName] = callback;
      
      // Cleanup
      return () => {
        delete cellValidationCallbacksRef.current[fieldName];
      };
    }
  }, [fieldName, cellValidationCallbacksRef]);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    onValueChange(newValue);
  };

  return (
    <div>
      <input
        value={value}
        onChange={e => handleChange(e.target.value)}
        onBlur={() => onValueChange(value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            onValueChange(value);
          }
        }}
        className={`border rounded px-2 py-1 w-full ${localError ? 'border-red-500' : ''}`}
      />
      <div className="text-red-500 text-xs mt-1 h-4">
        {localError && errorMessage ? errorMessage : ''}
      </div>
    </div>
  );
};

type EditableCheckboxProps = {
  user: User;
  editingUserHiddenRef: React.MutableRefObject<boolean>;
  editUserValuesRef: React.MutableRefObject<Partial<User>>;
};

const EditableCheckbox: React.FC<EditableCheckboxProps> = ({ user, editingUserHiddenRef, editUserValuesRef }) => {
  const [checked, setChecked] = React.useState(editingUserHiddenRef.current);
  const L = useLocalizedStrings();

  const handleChange = React.useCallback((newChecked: boolean) => {
    setChecked(newChecked);
    editingUserHiddenRef.current = newChecked;
    
    // Update the draft state for hidden status
    let currentMetadata = {};
    try {
      currentMetadata = user.metadata ? JSON.parse(user.metadata) : {};
    } catch (e) {
      currentMetadata = {};
    }
    
    const updatedMetadata = {
      ...currentMetadata,
      HiddenFromNonAdmins: newChecked,
    };
    
    // Store in editUserValuesRef so it gets saved with other changes
    editUserValuesRef.current.metadata = JSON.stringify(updatedMetadata);
  }, [user.metadata, editingUserHiddenRef, editUserValuesRef]);

  return (
    <div className="flex items-center justify-center pb-3">
      <Checkbox
        className='w-5 h-5'
        checked={checked}
        label=''
        onChange={e => handleChange(!checked)}
      />
    </div>
  );
};

export default function UserManagementPage() {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const getNav = usePost("/navigation");

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (Array.isArray(result?.navigation)) {
      setNav(result.navigation);
    }
  }, []);
  
  return (
    <ThreeColumnPage
      nav={nav}
      isDashboard={true}
      title="User Management"
      main={<UserManagement />}
    />
  );
}
